import React from 'react';
import { Container } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import TournamentSchedule from '../components/TournamentSchedule';

const Tournament: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Tournament - Ashridge Park FC</title>
        <meta
          name="description"
          content="Ashridge 2026 Tournament — June 6–7, 2026 at St Crispins School, Wokingham. Live scores, schedule, and location."
        />
      </Helmet>

      <style>{`
        .glass-registration-container {
          min-height: 100vh;
          padding: 1.5rem 0;
          background: linear-gradient(135deg, var(--navy-900) 0%, var(--navy-800) 55%, var(--navy-700) 100%);
        }

        .glass-card {
          background: rgba(255, 255, 255, 0.06);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-radius: var(--r-lg);
          border: 1px solid rgba(255, 255, 255, 0.14);
          box-shadow: var(--sh-lg);
          padding: 1.5rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .glass-title {
          color: #fff;
          font-family: var(--font-head);
          font-size: 2rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          margin-bottom: 0.5rem;
          text-align: center;
        }

        .glass-subtitle {
          color: rgba(255, 255, 255, 0.8);
          font-size: 0.95rem;
          margin-bottom: 1rem;
          text-align: center;
        }

        .tournament-badge {
          display: block;
          margin: 0 auto 1rem;
          width: 120px;
          height: auto;
          filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.4));
        }

        .glass-button {
          display: inline-block;
          background: linear-gradient(135deg, var(--navy-500) 0%, var(--navy) 100%);
          border: 2px solid rgba(255, 255, 255, 0.35);
          border-radius: var(--r-sm);
          color: #fff;
          padding: 0.875rem 2.5rem;
          font-family: var(--font-body);
          font-weight: 700;
          font-size: 1.05rem;
          transition: all 0.3s ease;
          box-shadow: 0 6px 18px -6px rgba(19, 42, 82, 0.7);
          text-decoration: none;
        }

        .glass-button:hover {
          background: linear-gradient(135deg, var(--navy) 0%, var(--navy-700) 100%);
          transform: translateY(-2px);
          box-shadow: 0 10px 26px -8px rgba(19, 42, 82, 0.8);
        }

        .glass-button:disabled,
        .glass-button.glass-button-disabled {
          opacity: 0.55;
          cursor: not-allowed;
          background: rgba(255, 255, 255, 0.15);
          box-shadow: none;
        }

        .scoring-link-wrap {
          text-align: center;
          margin-bottom: 2rem;
        }

        .live-scores-button {
          display: inline-flex;
          align-items: center;
          gap: 0.85rem;
          background: linear-gradient(135deg, var(--navy-700) 0%, var(--navy-500) 100%);
          background-size: 200% 200%;
          animation: liveGradient 6s ease infinite;
          border: 2px solid rgba(255, 255, 255, 0.4);
          border-radius: var(--r-sm);
          color: #fff;
          padding: 0.9rem 1.75rem;
          font-family: var(--font-body);
          font-weight: 700;
          font-size: 1.1rem;
          text-decoration: none;
          box-shadow: 0 6px 22px -6px rgba(19, 42, 82, 0.65);
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }

        .live-scores-button:hover {
          color: #fff;
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 10px 30px -8px rgba(19, 42, 82, 0.85);
        }

        .live-scores-button:hover .live-scores-arrow {
          transform: translateX(4px);
        }

        .live-pulse {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          background: rgba(255, 59, 48, 0.95);
          color: #fff;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          padding: 0.2rem 0.55rem;
          border-radius: 999px;
          text-transform: uppercase;
        }

        .live-pulse-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7);
          animation: livePulseDot 1.4s ease-out infinite;
        }

        .live-scores-arrow {
          font-size: 1.3rem;
          transition: transform 0.25s ease;
        }

        @keyframes liveGradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        @keyframes livePulseDot {
          0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7); }
          70% { box-shadow: 0 0 0 10px rgba(255, 255, 255, 0); }
          100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
        }

        .tournament-map {
          height: 350px;
          margin-bottom: 2rem;
        }
      `}</style>

      <div className="glass-registration-container">
        <Container>
          <div className="glass-card">
            <br/>
            <h2 className="glass-title">Ashridge 2026 Tournament</h2>
            <p className="glass-subtitle">June 6–7, 2026</p>
            <p className="glass-subtitle" style={{ fontSize: '1rem' }}>
              <i className="bi bi-geo-alt-fill"></i> St Crispins School, Wokingham
            </p>

            <div className="scoring-link-wrap">
              <a
                href="https://tournacore.com/t/ashridge-summer-tournament-2026"
                target="_blank"
                rel="noopener noreferrer"
                className="live-scores-button"
              >
                <span className="live-pulse">
                  <span className="live-pulse-dot"></span>
                  LIVE
                </span>
                <span className="live-scores-label">
                  <i className="bi bi-trophy-fill me-2"></i>
                  Live Scores &amp; Results
                </span>
                <i className="bi bi-arrow-right-circle-fill live-scores-arrow"></i>
              </a>
            </div>

            <div className="tournament-map">
              <iframe
                src="https://www.google.com/maps/embed/v1/place?q=place_id:ChIJkSI5Hz-BdkgRwB8TdzYEPAM&key=AIzaSyANv2W59WYEoo-8kTOmBAwVTOlu4NS3dDU"
                style={{ border: 0, borderRadius: '15px', width: '100%', height: '100%' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="St Crispins School"
              ></iframe>
            </div>

            <TournamentSchedule defaultOpen={true} />
          </div>
        </Container>
      </div>
    </>
  );
};

export default Tournament;
